/**
 * Provision taxonomy — build doc §4.5, LOCKED. Mirrors @argus/shared-types.
 * Duplicated here (rather than imported) so the engine stays dependency-free
 * and usable from Deno edge functions via npm specifier without workspace links.
 * A unit test in shared-types asserts the two lists stay identical.
 */

export const PROVISION_CATEGORIES = [
  "mgmt_fee_discount",
  "carry_modification",
  "gp_commitment",
  "expense_cap",
  "org_expense_cap",
  "mfn",
  "transparency",
  "advisory_committee",
  "key_person",
  "reporting_freq",
  "co_invest",
  "redemption",
  "transfer",
  "regulatory_rep",
  "tax_rep",
  "structural",
  "esg",
  "sanctions",
  "confidentiality",
  "indemnity",
  "dispute",
  "most_favored_nation_express",
  "other",
] as const;

export type ProvisionCategory = (typeof PROVISION_CATEGORIES)[number];

/** Categories an "economic terms only" MFN reaches. */
export const ECONOMIC_CATEGORIES: readonly ProvisionCategory[] = [
  "mgmt_fee_discount",
  "carry_modification",
  "gp_commitment",
  "expense_cap",
  "org_expense_cap",
];

/**
 * Keyword patterns for the rules classifier. Each match adds its weight; the
 * top-scoring category wins. Patterns run against the lowercased clause text.
 * Order within a category does not matter.
 */
export const CATEGORY_PATTERNS: Record<
  Exclude<ProvisionCategory, "other">,
  { pattern: RegExp; weight: number }[]
> = {
  mgmt_fee_discount: [
    { pattern: /management fee/, weight: 3 },
    { pattern: /reduced to|reduction of|discount(?:ed)? (?:to|of)|rebate/, weight: 2 },
    { pattern: /per annum/, weight: 1 },
  ],
  carry_modification: [
    { pattern: /carried interest|carry/, weight: 3 },
    { pattern: /hurdle|preferred return/, weight: 2 },
    { pattern: /waterfall/, weight: 1 },
  ],
  gp_commitment: [
    { pattern: /general partner('s)? (?:capital )?commitment|gp commitment/, weight: 4 },
    { pattern: /sponsor commitment/, weight: 3 },
  ],
  expense_cap: [
    { pattern: /(?:fund|partnership) expenses/, weight: 3 },
    { pattern: /cap(?:ped)? (?:at|on)|shall not exceed/, weight: 2 },
  ],
  org_expense_cap: [
    // Lookbehind: "excluding organizational expenses" inside a fund-expense cap
    // is a carve-out, not the subject of the provision.
    { pattern: /(?<!excluding )(?:organizational|organisational) expenses/, weight: 4 },
    { pattern: /cap(?:ped)? (?:at|on)|shall not exceed/, weight: 2 },
  ],
  mfn: [
    { pattern: /most[- ]favou?red[- ]nation/, weight: 5 },
    { pattern: /mfn/, weight: 4 },
    { pattern: /elect to receive the benefit/, weight: 3 },
    { pattern: /rights or benefits (?:that are )?more favou?rable/, weight: 3 },
  ],
  transparency: [
    { pattern: /information rights?/, weight: 3 },
    { pattern: /access to (?:the )?(?:books|records)/, weight: 3 },
    { pattern: /underlying portfolio (?:company )?information/, weight: 2 },
    { pattern: /transparency/, weight: 2 },
  ],
  advisory_committee: [
    { pattern: /advisory (?:committee|board)|lpac/, weight: 4 },
    { pattern: /observer (?:seat|status|rights)/, weight: 3 },
  ],
  key_person: [
    { pattern: /key (?:person|man|individual)/, weight: 5 },
    { pattern: /devote substantially all/, weight: 2 },
    { pattern: /suspension period/, weight: 1 },
  ],
  reporting_freq: [
    {
      pattern: /(?:quarterly|monthly|annual) (?:financial )?(?:reports?|statements?|reporting)/,
      weight: 3,
    },
    { pattern: /reporting (?:format|frequency|package)/, weight: 3 },
    { pattern: /ilpa (?:reporting )?template/, weight: 3 },
  ],
  co_invest: [
    { pattern: /co[- ]invest(?:ment)?/, weight: 5 },
    { pattern: /opportunit(?:y|ies) to invest alongside/, weight: 3 },
  ],
  redemption: [{ pattern: /redemption|withdraw(?:al)? rights?/, weight: 4 }],
  transfer: [
    { pattern: /transfer of (?:its |the |any )?(?:interest|commitment)/, weight: 4 },
    { pattern: /transfer (?:its|their) (?:interest|commitment)/, weight: 4 },
    { pattern: /assignment of (?:the )?interest/, weight: 3 },
    { pattern: /consent to (?:any )?transfer/, weight: 2 },
  ],
  regulatory_rep: [
    { pattern: /erisa|bhca|bank holding company|public plan|vcoc|plan assets?/, weight: 4 },
    { pattern: /freedom of information|foia|public records law/, weight: 3 },
    { pattern: /regulatory (?:representation|requirement|status)/, weight: 2 },
  ],
  tax_rep: [
    {
      pattern: /\bubti\b|\beci\b|effectively connected income|unrelated business taxable/,
      weight: 4,
    },
    { pattern: /tax (?:representation|covenant|status|treatment)/, weight: 3 },
    { pattern: /k-1|tax (?:estimates?|information)/, weight: 2 },
  ],
  structural: [
    {
      pattern: /parallel (?:fund|vehicle)|alternative investment vehicle|\baiv\b|feeder/,
      weight: 4,
    },
    { pattern: /side car|sidecar/, weight: 3 },
  ],
  esg: [
    { pattern: /\besg\b|environmental, social/, weight: 5 },
    { pattern: /responsible invest(?:ment|ing)|sustainability|sfdr/, weight: 3 },
    { pattern: /exclusion (?:list|policy)/, weight: 2 },
  ],
  sanctions: [{ pattern: /sanctions|ofac|anti[- ]money laundering|\baml\b/, weight: 4 }],
  confidentiality: [
    { pattern: /confidential(?:ity)?/, weight: 4 },
    { pattern: /non[- ]disclosure/, weight: 3 },
  ],
  indemnity: [
    { pattern: /indemnif(?:y|ication|ied)/, weight: 4 },
    { pattern: /exculpation/, weight: 3 },
  ],
  dispute: [{ pattern: /arbitration|dispute resolution|governing law|jurisdiction/, weight: 3 }],
  most_favored_nation_express: [
    // Distinct from `mfn`: explicit this-fund-only MFN language. The extractor
    // resolves the mfn/most_favored_nation_express split after value parsing.
    {
      pattern:
        /most[- ]favou?red[- ]nation.*(?:solely|only) (?:with respect to|in respect of) (?:the|this) (?:fund|partnership)/,
      weight: 6,
    },
  ],
};

/** Human labels for UI rendering. */
export const CATEGORY_LABELS: Record<ProvisionCategory, string> = {
  mgmt_fee_discount: "Management fee discount",
  carry_modification: "Carry modification",
  gp_commitment: "GP commitment",
  expense_cap: "Expense cap",
  org_expense_cap: "Org expense cap",
  mfn: "MFN",
  transparency: "Transparency / information rights",
  advisory_committee: "Advisory committee",
  key_person: "Key person",
  reporting_freq: "Reporting frequency",
  co_invest: "Co-investment",
  redemption: "Redemption",
  transfer: "Transfer",
  regulatory_rep: "Regulatory representation",
  tax_rep: "Tax representation",
  structural: "Structural",
  esg: "ESG",
  sanctions: "Sanctions / AML",
  confidentiality: "Confidentiality",
  indemnity: "Indemnification",
  dispute: "Dispute resolution",
  most_favored_nation_express: "MFN (this fund only, express)",
  other: "Other / unclassified",
};
